import { client } from './amplifyClient';
import { appsyncPublicGraphqlRequest } from './appsyncPublicGraphql';
import { createNotification } from './notifications';
import { createCareAssignment } from './careAssignments';
import { EmailService } from '../services/emailService';
import { getTherapistProfilePhotoResolverUrl } from './therapistPhotoResolver';
import { buildParentReferralUrl, getParentInviteOrigin } from './parentInviteOrigin';
import { v4 as uuidv4 } from 'uuid';

export type AssignmentStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

export type ParentQuestionnaireAssignmentRow = {
  id: string;
  kidProfileId: string;
  organizationId?: string | null;
  assignedByUserId: string;
  assigneeEmail: string;
  parentUserId?: string | null;
  invitationId?: string | null;
  assignmentType?: string | null;
  questionnaireId: string;
  status: AssignmentStatus;
  dueAt?: string | null;
  completedAt?: string | null;
  submissionSessionId?: string | null;
  therapistNotifiedAt?: string | null;
  emailSentAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

const ACTIVE_STATUSES: AssignmentStatus[] = ['PENDING', 'IN_PROGRESS'];

const ASSIGNMENT_SELECTION = `
  id
  kidProfileId
  organizationId
  assignedByUserId
  assigneeEmail
  parentUserId
  invitationId
  assignmentType
  questionnaireId
  status
  dueAt
  completedAt
  submissionSessionId
  therapistNotifiedAt
  emailSentAt
  createdAt
  updatedAt
`;

function hasGeneratedAssignmentModel(): boolean {
  return Boolean(client.models?.ParentQuestionnaireAssignment);
}

function mapAssignment(raw: Partial<ParentQuestionnaireAssignmentRow> | null | undefined): ParentQuestionnaireAssignmentRow | null {
  if (!raw?.id || !raw.kidProfileId || !raw.questionnaireId || !raw.assigneeEmail || !raw.assignedByUserId) {
    return null;
  }
  return {
    id: String(raw.id),
    kidProfileId: String(raw.kidProfileId),
    organizationId: raw.organizationId ?? null,
    assignedByUserId: String(raw.assignedByUserId),
    assigneeEmail: String(raw.assigneeEmail),
    parentUserId: raw.parentUserId ?? null,
    invitationId: raw.invitationId ?? null,
    assignmentType: raw.assignmentType ?? null,
    questionnaireId: String(raw.questionnaireId),
    status: (raw.status as AssignmentStatus) || 'PENDING',
    dueAt: raw.dueAt ?? null,
    completedAt: raw.completedAt ?? null,
    submissionSessionId: raw.submissionSessionId ?? null,
    therapistNotifiedAt: raw.therapistNotifiedAt ?? null,
    emailSentAt: raw.emailSentAt ?? null,
    createdAt: raw.createdAt ?? null,
    updatedAt: raw.updatedAt ?? null,
  };
}

async function listAssignmentsGraphql(filter: Record<string, unknown>): Promise<ParentQuestionnaireAssignmentRow[]> {
  const all: ParentQuestionnaireAssignmentRow[] = [];
  let nextToken: string | null = null;
  do {
    const data = await appsyncPublicGraphqlRequest<{
      listParentQuestionnaireAssignments?: {
        items?: Array<Partial<ParentQuestionnaireAssignmentRow> | null> | null;
        nextToken?: string | null;
      };
    }>(
      `query ListParentQuestionnaireAssignments($filter: ModelParentQuestionnaireAssignmentFilterInput, $limit: Int, $nextToken: String) {
        listParentQuestionnaireAssignments(filter: $filter, limit: $limit, nextToken: $nextToken) {
          items { ${ASSIGNMENT_SELECTION} }
          nextToken
        }
      }`,
      { filter, limit: 500, nextToken },
    );
    for (const item of data.listParentQuestionnaireAssignments?.items || []) {
      const mapped = mapAssignment(item);
      if (mapped) all.push(mapped);
    }
    nextToken = data.listParentQuestionnaireAssignments?.nextToken ?? null;
  } while (nextToken);
  return all;
}

async function getAssignmentGraphql(id: string): Promise<ParentQuestionnaireAssignmentRow | null> {
  const data = await appsyncPublicGraphqlRequest<{
    getParentQuestionnaireAssignment?: Partial<ParentQuestionnaireAssignmentRow> | null;
  }>(
    `query GetParentQuestionnaireAssignment($id: ID!) {
      getParentQuestionnaireAssignment(id: $id) { ${ASSIGNMENT_SELECTION} }
    }`,
    { id },
  );
  return mapAssignment(data.getParentQuestionnaireAssignment);
}

async function createAssignmentGraphql(input: Record<string, unknown>): Promise<ParentQuestionnaireAssignmentRow> {
  const data = await appsyncPublicGraphqlRequest<{
    createParentQuestionnaireAssignment?: Partial<ParentQuestionnaireAssignmentRow> | null;
  }>(
    `mutation CreateParentQuestionnaireAssignment($input: CreateParentQuestionnaireAssignmentInput!) {
      createParentQuestionnaireAssignment(input: $input) { ${ASSIGNMENT_SELECTION} }
    }`,
    { input },
  );
  const mapped = mapAssignment(data.createParentQuestionnaireAssignment);
  if (!mapped) throw new Error('Questionnaire assignment was not created.');
  return mapped;
}

async function updateAssignmentGraphql(input: Record<string, unknown>): Promise<void> {
  await appsyncPublicGraphqlRequest(
    `mutation UpdateParentQuestionnaireAssignment($input: UpdateParentQuestionnaireAssignmentInput!) {
      updateParentQuestionnaireAssignment(input: $input) { id }
    }`,
    { input },
  );
}

async function listAllAssignments(filter: Record<string, unknown>) {
  if (!hasGeneratedAssignmentModel()) {
    return listAssignmentsGraphql(filter);
  }

  const all: ParentQuestionnaireAssignmentRow[] = [];
  let nextToken: string | undefined;
  do {
    const resp = await client.models.ParentQuestionnaireAssignment.list({
      filter,
      limit: 500,
      nextToken,
      authMode: 'apiKey',
    } as any);
    for (const row of resp.data || []) {
      const mapped = mapAssignment(row as ParentQuestionnaireAssignmentRow);
      if (mapped) all.push(mapped);
    }
    nextToken = resp.nextToken ?? undefined;
  } while (nextToken);
  return all;
}

export async function listAssignmentsForKid(kidProfileId: string): Promise<ParentQuestionnaireAssignmentRow[]> {
  const rows = await listAllAssignments({ kidProfileId: { eq: kidProfileId } });
  return rows.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
}

export async function getPendingAssignmentForParent(params: {
  parentUserId?: string | null;
  parentEmail?: string | null;
  kidProfileIds?: string[];
}): Promise<ParentQuestionnaireAssignmentRow | null> {
  const { parentUserId, parentEmail, kidProfileIds = [] } = params;
  const email = parentEmail?.trim().toLowerCase();

  const candidates: ParentQuestionnaireAssignmentRow[] = [];

  if (parentUserId) {
    const byUser = await listAllAssignments({ parentUserId: { eq: parentUserId } });
    candidates.push(...byUser);
  }

  if (email) {
    const byKid = await Promise.all(
      kidProfileIds.map((id) => listAllAssignments({ kidProfileId: { eq: id } })),
    );
    for (const rows of byKid) {
      candidates.push(...rows.filter((r) => r.assigneeEmail?.toLowerCase() === email));
    }
    if (!kidProfileIds.length) {
      const byEmail = await listAllAssignments({ assigneeEmail: { eq: email } });
      candidates.push(...byEmail);
    }
  }

  const pending = candidates.filter((r) => ACTIVE_STATUSES.includes(r.status));
  pending.sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''));
  return pending[0] ?? null;
}

export async function getAssignmentById(assignmentId: string): Promise<ParentQuestionnaireAssignmentRow | null> {
  if (!hasGeneratedAssignmentModel()) {
    return getAssignmentGraphql(assignmentId);
  }
  const resp = await client.models.ParentQuestionnaireAssignment.get(
    { id: assignmentId },
    { authMode: 'apiKey' },
  );
  return mapAssignment(resp.data as ParentQuestionnaireAssignmentRow | null);
}

async function createAssignment(input: Record<string, unknown>): Promise<void> {
  if (!hasGeneratedAssignmentModel()) {
    await createAssignmentGraphql(input);
    return;
  }
  await client.models.ParentQuestionnaireAssignment.create(input as any);
}

async function updateAssignment(input: Record<string, unknown>): Promise<void> {
  if (!hasGeneratedAssignmentModel()) {
    await updateAssignmentGraphql(input);
    return;
  }
  await client.models.ParentQuestionnaireAssignment.update(input as any);
}

function assignmentDeepLink(params: {
  kidProfileId: string;
  questionnaireId: string;
  assignmentId: string;
  forRegistration?: boolean;
  inviteToken?: string;
}): string {
  const assessmentPath = `/questionnaire/${params.kidProfileId}?assignmentId=${params.assignmentId}`;
  if (params.forRegistration && params.inviteToken) {
    // Always TinyWins parent origin (MiraPlus therapists invite parents onto TinyWins)
    return buildParentReferralUrl(params.inviteToken, { redirect: assessmentPath });
  }
  const origin = getParentInviteOrigin();
  // Home + Auth modal (Header). /parents hides Header so openAuth would not work there.
  const openAuth = params.forRegistration ? 'signUp' : 'signIn';
  return `${origin}/?returnTo=${encodeURIComponent(assessmentPath)}&openAuth=${openAuth}`;
}

async function sendQuestionnaireAssignmentEmail(params: {
  to: string;
  childName: string;
  questionnaireName: string;
  actionUrl: string;
  organizationName?: string;
  therapistName?: string;
  isNewUser: boolean;
}): Promise<boolean> {
  // Prefer existing Lambda Function URL (server-side MailerSend — no browser CORS).
  try {
    const endpoint = await getTherapistProfilePhotoResolverUrl();
    if (endpoint) {
      // text/plain avoids CORS preflight — Function URL Allow-Headers is empty.
      const res = await fetch(endpoint.replace(/\/$/, ''), {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({
          action: 'sendAssignmentEmail',
          to: params.to,
          childName: params.childName,
          questionnaireName: params.questionnaireName,
          actionUrl: params.actionUrl,
          organizationName: params.organizationName,
          therapistName: params.therapistName,
          isNewUser: params.isNewUser,
        }),
      });
      const json = (await res.json().catch(() => ({}))) as { sent?: boolean; ok?: boolean; error?: string };
      if (res.ok && (json.sent || json.ok)) return true;
      console.warn('Assignment email via Lambda failed:', json.error || res.statusText);
    }
  } catch (e) {
    console.warn('Assignment email Lambda call error:', e);
  }

  // Fallback: same-origin MailerSend proxy (may fail if Amplify strips Authorization).
  try {
    return await EmailService.sendQuestionnaireAssignmentEmail(params);
  } catch (e) {
    console.warn('Assignment email proxy failed:', e);
    return false;
  }
}

export async function assignQuestionnaireToParent(params: {
  kidProfileId: string;
  kidName: string;
  organizationId: string;
  organizationName?: string;
  assignedByUserId: string;
  assigneeEmail: string;
  parentUserId?: string | null;
  questionnaireId: string;
  questionnaireName: string;
  therapistName?: string;
}): Promise<{ assignmentId: string; emailSent: boolean; actionUrl: string }> {
  const email = params.assigneeEmail.trim().toLowerCase();
  if (!email) throw new Error('Parent email is required.');

  const existing = await listAssignmentsForKid(params.kidProfileId);
  const duplicate = existing.find(
    (a) =>
      ACTIVE_STATUSES.includes(a.status) &&
      a.questionnaireId === params.questionnaireId &&
      a.assigneeEmail.toLowerCase() === email,
  );
  if (duplicate) {
    throw new Error('This parent already has a pending assignment for this questionnaire.');
  }

  const now = new Date().toISOString();
  const assignmentId = uuidv4();
  let emailSent = false;

  // Do not create UserInvitation here — that triggers the generic "Invite Parent" email.
  // Questionnaire email below carries the deep link (register/login → assessment).
  const needsRegistrationInvite = !params.parentUserId;

  await createAssignment({
    id: assignmentId,
    kidProfileId: params.kidProfileId,
    organizationId: params.organizationId,
    assignedByUserId: params.assignedByUserId,
    assigneeEmail: email,
    parentUserId: params.parentUserId ?? undefined,
    assignmentType: 'CUSTOM_QUESTIONNAIRE',
    questionnaireId: params.questionnaireId,
    status: 'PENDING',
    createdAt: now,
    updatedAt: now,
  });

  const link = assignmentDeepLink({
    kidProfileId: params.kidProfileId,
    questionnaireId: params.questionnaireId,
    assignmentId,
    forRegistration: needsRegistrationInvite,
  });

  emailSent = await sendQuestionnaireAssignmentEmail({
    to: email,
    childName: params.kidName,
    questionnaireName: params.questionnaireName,
    organizationName: params.organizationName,
    therapistName: params.therapistName,
    actionUrl: link,
    isNewUser: needsRegistrationInvite,
  });

  if (emailSent) {
    await updateAssignment({
      id: assignmentId,
      emailSentAt: now,
      updatedAt: now,
    });
  }

  if (params.parentUserId) {
    await createNotification({
      toUserId: params.parentUserId,
      fromUserId: params.assignedByUserId,
      organizationId: params.organizationId,
      kidProfileId: params.kidProfileId,
      notificationType: 'ASSESSMENT_ASSIGNED',
      title: `Complete assessment: ${params.questionnaireName}`,
      body: `Your therapist assigned "${params.questionnaireName}" for ${params.kidName}.`,
      actionUrl: `/questionnaire/${params.kidProfileId}?assignmentId=${assignmentId}`,
      payload: { assignmentId, questionnaireId: params.questionnaireId },
    });
  }

  try {
    await createCareAssignment({
      kidProfileId: params.kidProfileId,
      assignedByUserId: params.assignedByUserId,
      parentUserId: params.parentUserId,
      organizationId: params.organizationId,
      artifactType: 'SCREENER',
      title: params.questionnaireName,
      questionnaireAssignmentId: assignmentId,
      notifyParentUserId: null, // already notified above
      actionUrl: `/questionnaire/${params.kidProfileId}?assignmentId=${assignmentId}`,
    });
  } catch (err) {
    console.warn('CareAssignment upsert for screener failed:', err);
  }

  return { assignmentId, emailSent, actionUrl: link };
}

export async function markAssignmentInProgress(assignmentId: string): Promise<void> {
  const row = await getAssignmentById(assignmentId);
  if (!row || row.status !== 'PENDING') return;
  await updateAssignment({
    id: assignmentId,
    status: 'IN_PROGRESS',
    updatedAt: new Date().toISOString(),
  });
}

export async function completeAssignment(params: {
  assignmentId: string;
  submissionSessionId: string;
  parentUserId?: string | null;
  therapistUserId: string;
  kidProfileId: string;
  organizationId?: string | null;
  questionnaireName: string;
  childName?: string;
  parentName?: string;
}): Promise<void> {
  const now = new Date().toISOString();

  await updateAssignment({
    id: params.assignmentId,
    status: 'COMPLETED',
    completedAt: now,
    submissionSessionId: params.submissionSessionId,
    parentUserId: params.parentUserId ?? undefined,
    updatedAt: now,
  });

  await createNotification({
    toUserId: params.therapistUserId,
    fromUserId: params.parentUserId ?? undefined,
    organizationId: params.organizationId ?? undefined,
    kidProfileId: params.kidProfileId,
    notificationType: 'ASSESSMENT_COMPLETED',
    title: `Assessment completed: ${params.questionnaireName}`,
    body: params.parentName
      ? `${params.parentName} completed "${params.questionnaireName}" for ${params.childName || 'your client'}.`
      : `A parent completed "${params.questionnaireName}".`,
    actionUrl: `/client-control/${params.kidProfileId}#all-intake`,
    payload: {
      assignmentId: params.assignmentId,
      submissionSessionId: params.submissionSessionId,
    },
  });

  try {
    const therapist = await client.models.User.get({ id: params.therapistUserId }, { authMode: 'apiKey' });
    const therapistEmail = therapist.data?.email;
    if (therapistEmail) {
      await EmailService.sendAssessmentCompletedEmail({
        to: therapistEmail,
        questionnaireName: params.questionnaireName,
        childName: params.childName || 'your client',
        actionUrl: `${typeof window !== 'undefined' ? window.location.origin : 'https://www.tinywins.ai'}/client-control/${params.kidProfileId}#all-intake`,
      });
      await updateAssignment({
        id: params.assignmentId,
        therapistNotifiedAt: now,
      });
    }
  } catch (e) {
    console.warn('Therapist completion email failed:', e);
  }
}
